import React, {useState} from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, Typography, Box, Card, Alert, Divider
} from '@mui/material';
import {
    Language as LanguageIcon,
    Map as MapIcon,
    Search as SearchIcon,
    Place as PlaceIcon,
    CloudUpload as UploadIcon,
    ArrowForward as ArrowIcon
} from '@mui/icons-material';
import LanguageSelector from './LanguageSelector';

const WelcomeDialog = ({
                           open,
                           onGetStarted,
                           languages,
                           selectedLanguage,
                           customLanguage,
                           customLanguageCode,
                           customLanguageObject,
                           onLanguageChange,
                           onCustomLanguageChange,
                           onCustomLanguageCodeChange
                       }) => {
    const hasValidLanguage = selectedLanguage?.ietfLanguageTag ||
        (customLanguageObject?.customLabel && customLanguageObject?.customLanguageCode);

    const handleGetStarted = () => {
        if (hasValidLanguage) {
            onGetStarted();
        }
    };

    const steps = [
        {
            number: "1",
            icon: <LanguageIcon/>,
            title: "Select Your Language",
            description: "Choose your native language below to get started. This helps us show you places that need local names in your language.",
            color: "#2563eb"
        },
        {
            number: "2",
            icon: <MapIcon/>,
            title: "Choose Place Type & Navigate",
            description: "Select what type of places you want to work with (cities, villages, etc.) and navigate the map to your region of interest.",
            color: "#059669"
        },
        {
            number: "3",
            icon: <SearchIcon/>,
            title: "Search for Places",
            description: "Click the Search button to load places in your selected area that don't have names in your language yet.",
            color: "#dc2626"
        },
        {
            number: "4",
            icon: <PlaceIcon/>,
            title: "Add Local Names",
            description: "Click on any map pin to open a dialog where you can add the local name (endonym) for that place.",
            color: "#7c3aed"
        },
        {
            number: "5",
            icon: <UploadIcon/>,
            title: "Save, Share or Download",
            description: "Your contributions are saved locally in a list. and can be exported or uploaded directly to Wikidata to help researchers worldwide.  You can download a JSON file with your contributions for your own research.",
            color: "#ea580c"
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={false} // Prevent closing until language is selected
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogContent sx={{p: 4}}>
                {/* Header */}
                <Box sx={{textAlign: 'center', mb: 4}}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 2
                    }}>
                        <LanguageIcon sx={{fontSize: 40, color: 'primary.main'}}/>
                        <Typography variant="h4" sx={{fontWeight: 700, color: '#1e293b'}}>
                            Welcome to EndoLink
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{color: '#64748b', maxWidth: 500, mx: 'auto'}}>
                        Learn how to contribute local place names (endonyms) to preserve linguistic heritage
                    </Typography>
                </Box>

                {/* Language Selection */}
                <Card sx={{mb: 4, p: 3, backgroundColor: '#f0f9ff', border: '2px solid #2563eb'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                        <LanguageIcon sx={{color: 'primary.main'}}/>
                        <Typography variant="h6" sx={{fontWeight: 600}}>
                            Step 1: Select Your Language
                        </Typography>
                    </Box>

                    <LanguageSelector
                        languages={languages}
                        selectedLanguage={selectedLanguage}
                        customLanguage={customLanguage}
                        customLanguageCode={customLanguageCode}
                        customLanguageObject={customLanguageObject}
                        onLanguageChange={onLanguageChange}
                        onCustomLanguageChange={onCustomLanguageChange}
                        onCustomLanguageCodeChange={onCustomLanguageCodeChange}
                    />

                    {!hasValidLanguage && (
                        <Alert severity="info" sx={{mt: 2, borderRadius: 2}}>
                            <Typography variant="body2">
                                Please select a language to continue and start contributing to EndoLink.
                            </Typography>
                        </Alert>
                    )}

                    {hasValidLanguage && (
                        <Alert severity="success" sx={{mt: 2, borderRadius: 2}}>
                            <Typography variant="body2">
                                <strong>Good to know:</strong> You can change your language selection anytime later
                                using the dropdown menu above the map.
                            </Typography>
                        </Alert>
                    )}
                </Card>

                {/* Steps Guide */}
                <Typography variant="h6" sx={{fontWeight: 600, mb: 3, textAlign: 'center'}}>
                    How it works in 5 simple steps:
                </Typography>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    {steps.map((step, index) => (
                        <Card key={index} sx={{
                            p: 3,
                            border: index === 0 ? '2px solid #2563eb' : '1px solid #e2e8f0',
                            backgroundColor: index === 0 ? '#f0f9ff' : 'white'
                        }}>
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2}}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: step.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.2rem',
                                    flexShrink: 0
                                }}>
                                    {step.number}
                                </Box>
                                <Box sx={{flex: 1}}>
                                    <Typography variant="h6" sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: '#1e293b'
                                    }}>
                                        {step.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: '#64748b',
                                        lineHeight: 1.5
                                    }}>
                                        {step.description}
                                    </Typography>
                                </Box>
                                <Box sx={{color: step.color, flexShrink: 0}}>
                                    {step.icon}
                                </Box>
                            </Box>
                        </Card>
                    ))}
                </Box>

                <Divider sx={{my: 3}}/>
            </DialogContent>

            <DialogActions sx={{p: 3, justifyContent: 'center', backgroundColor: 'rgba(248, 250, 252, 0.5)'}}>
                <Button
                    onClick={handleGetStarted}
                    variant="contained"
                    size="large"
                    endIcon={<ArrowIcon/>}
                    disabled={!hasValidLanguage}
                    sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        background: hasValidLanguage
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : undefined,
                        '&:hover': hasValidLanguage ? {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        } : undefined
                    }}
                >
                    {hasValidLanguage ? 'Start Contributing to EndoLink' : 'Select a Language to Continue'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WelcomeDialog;